import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from '@phosphor-icons/react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import SEO from '../components/SEO';
import { SectionHero, SectionHeading, StatsRow, CTABanner } from '../components/SiteBlocks';
import { Button } from '../components/ui/button';

export default function AboutPage() {
  return (
    <div className="min-h-screen" style={{ background: 'var(--background)', color: 'var(--text-primary)' }}>
      <SEO path="/about" title="About Xceed — From Offshore Delivery to Enterprise AI Automation" description="Xceed IT Solutions: two decades spanning offshore technology services (Fortune 500 development labs since 2005), MobiQuest mobile loyalty (Paytm m'Loyalty, 130M members), and today's enterprise AI automation — led by the same founding team." />
      <Navbar />

      <SectionHero
        eyebrow="About XceedAgents"
        title="The hybrid AI + human engineering company."
        subtitle="We're a team of senior engineers and specialist AI agents working as one. We exist to make software delivery 60% faster, 50% cheaper, and dramatically better — without compromising on quality, ownership, or trust."
        topContent={
          <div className="mb-10 flex justify-center lg:justify-start" data-testid="about-logo-splash">
            <div className="inline-block bg-white rounded-2xl px-10 py-6 shadow-xl">
              <img
                src="/brand/xceed-logo-full.png"
                alt="XceedAgents.ai — full logo lockup"
                className="h-28 sm:h-36 w-auto"
              />
            </div>
          </div>
        }
      >
        <Link to="/start-project"><Button className="h-12 px-7 font-semibold hover-lift gap-2" style={{ background: 'var(--primary-accent)', color: '#fff' }}>Work with us <ArrowRight size={16} weight="bold" /></Button></Link>
      </SectionHero>

      <StatsRow stats={[
        { value: '500+', label: 'Products Shipped' },
        { value: '98%', label: 'Client Satisfaction' },
        { value: '40+', label: 'Countries Served' },
        { value: '8', label: 'AI Agents per Engineer' },
      ]} />

      {/* ============ XCEED HERITAGE ============ */}
      <section className="py-20 lg:py-28" style={{ background: 'var(--surface)' }} data-testid="xceed-heritage-section">
        <div className="max-w-5xl mx-auto px-6">
          <SectionHeading
            eyebrow="About Xceed"
            title="A foundation in technology. A journey of transformation."
            subtitle="Exceeding expectations through speed, innovation and efficiency."
          />
          <div className="mt-8 space-y-5 text-lg leading-relaxed" style={{ color: 'var(--text-primary)' }}>
            <p>
              <strong>Xceed IT Solutions</strong> is the parent company behind a journey spanning two decades of
              offshore technology services, mobile loyalty and enterprise mobile automation. Through each phase, our
              purpose has remained consistent: helping businesses grow through technology that delivers measurable value.
            </p>
          </div>

          {/* Timeline */}
          <div className="mt-12 space-y-0">
            {[
              {
                year: '2005',
                title: 'Xceed is founded',
                body: 'Our story began in August 2005, when Vineet Narang & Sabina Kamal founded Xceed under the guidance and advisory mentorship of late Dr K.C. Narang, Chairman of Xceed & MobiQuest. With an Offshore Development Centre in Noida, India, the company built dedicated virtual teams for global customers across product development and maintenance, application development, e-learning, technical support and telesales.',
              },
              {
                year: '2005–2008',
                title: 'Fortune 500 development labs',
                body: 'Xceed served Fortune 500 organisations and midsized software product companies, establishing dedicated development labs that operated as secure extensions of their customers\' teams. By connecting onsite and offshore expertise, we helped businesses accelerate delivery, improve efficiency and reduce costs — establishing the foundations that continue to guide us: customer understanding, disciplined execution and a commitment to innovation.',
              },
              {
                year: '2008',
                title: 'MobiQuest — last-mile mobile data automation',
                body: 'The same management team founded MobiQuest, recognising the potential of mobile technology to capture last-mile data, integrate information across web, mobile and physical offline channels, and transform how businesses engage and retain customers. Xceed became part of the MobiQuest family, bringing its technology capabilities and delivery experience into this next chapter. MobiQuest scaled through industry partnerships with OEMs and telcos such as BlackBerry and Airtel, serving companies like Walmart, Abbott and Transasia for last-mile data automation across sales force, KYC and service technicians.',
              },
              {
                year: '2017',
                title: "m'Loyal → Paytm m'Loyalty",
                body: "The founders built and scaled the m'Loyal platform, helping businesses turn customer relationships into sustained engagement and growth. Following a strategic partnership and investment by Paytm in 2017, the platform was renamed Paytm m'Loyalty. Under the founders' leadership, it scaled to 130 million active members and ₹5 billion worth of loyalty points, before the founders completed a successful exit.",
              },
              {
                year: 'Today',
                title: 'Enterprise AI automation',
                body: 'Following that exit, the founders took ownership of Xceed again, building on the experience gained from developing, operating and scaling enterprise technology platforms. Today, Xceed is evolving into an enterprise AI automation company — helping organisations modernise legacy processes, connect systems and data, and embed intelligence into everyday operations. Our focus is on translating AI\'s potential into practical business outcomes: greater productivity, faster decisions, better customer experiences and scalable growth, driven by seasoned professionals with a proven track record.',
              },
            ].map((m, i) => (
              <div key={i} className="relative pl-10 pb-10 last:pb-0" data-testid={`heritage-milestone-${i}`}>
                {i < 4 && (
                  <span className="absolute left-[7px] top-5 bottom-0 w-px" style={{ background: 'var(--border-strong)' }} />
                )}
                <span className="absolute left-0 top-1.5 w-[15px] h-[15px] rounded-full border-4" style={{ borderColor: 'var(--primary-accent)', background: 'var(--surface)' }} />
                <div className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: 'var(--primary-accent)' }}>{m.year}</div>
                <h3 className="font-heading text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>{m.title}</h3>
                <p className="text-base leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{m.body}</p>
              </div>
            ))}
          </div>

          {/* Legacy stats */}
          <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { v: '20+', l: 'Years in technology' },
              { v: '130M', l: "Paytm m'Loyalty active members" },
              { v: '₹5B', l: 'Loyalty points under management' },
              { v: 'Fortune 500', l: 'Clients served from day one' },
            ].map((s, i) => (
              <div key={i} className="p-6 rounded-xl border bg-white text-center" style={{ borderColor: 'var(--border)' }}>
                <div className="font-heading text-2xl font-bold" style={{ color: 'var(--primary-accent)' }}>{s.v}</div>
                <div className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>{s.l}</div>
              </div>
            ))}
          </div>

          <div className="mt-12 p-7 rounded-2xl border bg-white" style={{ borderColor: 'var(--border)' }}>
            <p className="text-lg leading-relaxed" style={{ color: 'var(--text-primary)' }}>
              The core management team remains the same, preserving the continuity of leadership, expertise and values
              across our journey. <strong>From offshore delivery to mobile loyalty to enterprise AI automation, Xceed
              continues to help businesses turn technological change into lasting business value.</strong>
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-6">
          <SectionHeading eyebrow="Our story" title="Why we built XceedAgents." />
          <div className="mt-8 space-y-5 text-lg leading-relaxed" style={{ color: 'var(--text-primary)' }}>
            <p>Traditional offshore vendors sell hours. They optimize for billable utilization, not outcomes. Their engineers are juniors with senior labels. Quality is inconsistent. Timezones don't align. Delivery is slow.</p>
            <p>Onshore agencies are excellent — but at $200-300/hour, only the well-funded can afford them. Even then, they ship at the speed of human typing.</p>
            <p>We saw an opportunity. <strong>What if every senior engineer was augmented by 8 specialist AI agents?</strong> Code, tests, reviews, design polish, deployment, documentation, customer support, analytics — all running in parallel, all reviewed by the human before merge.</p>
            <p>The result: <strong>60% faster delivery, 50% lower cost, world-class quality.</strong> That's XceedAgents.</p>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24" style={{ background: 'var(--surface)' }}>
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading eyebrow="Our principles" title="How we work." />
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {[
              { t: 'Senior engineers only', d: 'Every developer at XceedAgents has 5+ years of experience. Juniors are not assigned to client projects. Period.' },
              { t: 'AI as multiplier, never replacement', d: 'AI agents accelerate humans, not replace them. Every line of code is reviewed by a senior engineer before it ships.' },
              { t: 'Your code, your IP', d: 'You own 100% of the code from day 1. We commit to your repo. NDA-ready. No vendor lock-in, ever.' },
              { t: 'Transparency by default', d: 'Daily standups, Friday demos, live Linear/Jira boards. You see exactly what we\'re building, every day.' },
              { t: 'Outcomes, not hours', d: 'We sell shipped products, not timesheets. Fixed-scope projects with money-back guarantees on missed milestones.' },
              { t: 'Long-term partnership', d: 'We don\'t do one-and-done. Most of our clients stay with us 18+ months as we evolve their products together.' },
            ].map((p, i) => (
              <div key={i} className="p-7 rounded-2xl border bg-white" style={{ borderColor: 'var(--border)' }} data-testid={`principle-${i}`}>
                <div className="font-heading text-xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>{p.t}</div>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABanner title="Let's build something together." ctaText="Start a Project" ctaHref="/start-project" secondaryText="View Open Roles" secondaryHref="/careers" />
      <Footer />
    </div>
  );
}
